import { Anchor, Badge, createStyles, Group } from '@mantine/core';
import React from 'react';

import { PartnerIncidentType } from '@portals/api/partners';
import { DetailsPanel } from '@portals/core';
import {
  DetailsList,
  DetailsListProps,
  PriorityIcon,
  usePortalProtocolAndDomain,
} from '@portals/framework';
import { ReactComponent as Export } from '@portals/icons/linear/export-2.svg';
import {
  formatDateTime,
  getIncidentPriorityLevelColor,
  getIncidentPriorityLevelName,
  timeAgo,
} from '@portals/utils';

interface IncidentDetailsPanelProps {
  incident: PartnerIncidentType;
  onClosePanel: () => void;
}

export function IncidentDetailsPanel({
  incident,
  onClosePanel,
}: IncidentDetailsPanelProps) {
  const { classes } = useStyles();
  const { protocolAndDomain } = usePortalProtocolAndDomain();

  const DEVICE_URL = `${protocolAndDomain}/m/device/${incident.device_id}?org=${incident.org_id}`;

  const detailsList: DetailsListProps['items'] = [
    { label: 'Space', value: incident.space_name },
    { label: 'Created', value: timeAgo(Date.parse(incident.created_at)) },
    {
      label: 'Last Updated',
      value: timeAgo(Date.parse(incident.updated_at)),
    },
    {
      label: 'ID',
      value: incident.id,
    },
    {
      label: 'Priority',
      value: (
        <Badge
          radius="sm"
          size="lg"
          color={getIncidentPriorityLevelColor(incident.priority)}
          className={classes.priorityBadge}
        >
          {getIncidentPriorityLevelName(incident.priority)}
        </Badge>
      ),
    },
  ];

  const deviceDetailsList: DetailsListProps['items'] = [
    {
      label: 'Device',
      value: (
        <Anchor href={DEVICE_URL} target="_blank">
          {incident.device_name}
        </Anchor>
      ),
    },
    {
      label: 'Vendor',
      value: 'TBD',
    },
    {
      label: 'Model',
      value: incident.device_model_name,
    },
    {
      label: 'Last seen',
      value: formatDateTime(incident.device_last_seen_at),
    },
    {
      label: 'Firmware version',
      value: incident.device_firmware_version,
    },
  ];

  return (
    <DetailsPanel>
      <DetailsPanel.Header
        onClose={onClosePanel}
        title={
          <Group>
            <PriorityIcon priorityLevel={incident.priority} />
            <DetailsPanel.Title>{incident.title}</DetailsPanel.Title>
          </Group>
        }
      >
        <DetailsPanel.Actions>
          <DetailsPanel.ActionButton
            leftIcon={<Export />}
            onClick={() => {
              window.open(DEVICE_URL);
            }}
          >
            Show Device
          </DetailsPanel.ActionButton>
        </DetailsPanel.Actions>
      </DetailsPanel.Header>

      <DetailsPanel.Body>
        <DetailsPanel.Section
          title="Description"
          content={incident.description || '--'}
        />

        <DetailsList title="Information" items={detailsList} />

        <DetailsList title="Device details" items={deviceDetailsList} />
      </DetailsPanel.Body>
    </DetailsPanel>
  );
}

const useStyles = createStyles((theme) => ({
  priorityBadge: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 400,
  },
}));
